import { fetchOrders } from '@shared/api'
import { logger } from '@shared/lib/logger'
import { attempt } from '@shared/utils'
import { useAppData } from '@app/providers'
import { useIdentity } from '@vp/ubik-context'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useFulfillmentHistory } from './use-fulfillment-history'

export const useOrders = () => {
  const { auth } = useIdentity()
  const { setOrdersData, concordApiServiceUrl, concordTenantId } = useAppData()
  const { prefetch: prefetchFulfillment } = useFulfillmentHistory()
  const [isLoading, setIsLoading] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)
  const hasFetchedRef = useRef(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const prefetch = useCallback(async () => {
    if (hasFetchedRef.current) return

    const token = auth?.getAuthorizationHeader?.()

    if (!token) {
      logger.warn('Orders prefetch skipped: no auth token', {
        action: 'orders_prefetch_skipped',
        component: 'useOrders',
      })
      return
    }

    hasFetchedRef.current = true
    setHasFailed(false)
    setIsLoading(true)

    // Reset the fetched flag so a retry is possible.
    const fail = (message: string) => {
      hasFetchedRef.current = false
      if (isMountedRef.current) setHasFailed(true)
      logger.error('Orders prefetch failed', {
        action: 'orders_prefetch_failed',
        component: 'useOrders',
        error: message,
      })
    }

    const { data: orders, error: fetchError } = await attempt(fetchOrders(concordApiServiceUrl, token, concordTenantId))

    if (fetchError) {
      fail(fetchError.message)
      if (isMountedRef.current) setIsLoading(false)
      return
    }

    if (!isMountedRef.current) return

    setOrdersData(orders)

    const { error: prefetchError } = await attempt(prefetchFulfillment(orders))

    if (prefetchError) {
      fail(prefetchError.message)
    }

    if (isMountedRef.current) {
      setIsLoading(false)
    }
  }, [auth, setOrdersData, prefetchFulfillment, concordTenantId, concordApiServiceUrl])

  return { isLoading, hasFailed, prefetch }
}
